"use client";

// Bloc boosters d'XP du rail /reps. Un booster actif multiplie TOUT l'XP gagné
// (leagues comprises) pendant sa durée. Les boosters en stock s'activent au choix,
// un seul à la fois. La carte n'apparaît que si on a un booster (actif ou en stock).
//
// Le stock est présenté par LOTS, pas booster par booster : toutes les sources
// donnent le même ×2 de 30 min (achat en boutique, coffres), donc cinquante
// boosters faisaient cinquante lignes identiques. Un lot = un couple
// multiplicateur/durée, avec son compte.
//
// L'état vient du magasin partagé (hooks/useXpBoosters) : la pastille de la
// barre du haut montre le même inventaire sur la même page, les deux ne
// doivent jamais annoncer deux stocks différents ni faire deux appels pour la
// même réponse.
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import Image from "next/image";
import useXpBoosters, {
    EXPIRY_WARNING_DAYS,
    formatCountdown,
    groupBoosters,
} from "@/hooks/useXpBoosters";

// Au-delà, les lots se replient derrière un bouton. Le stock tient en un seul
// lot en pratique (toutes les sources donnent le même ×2 de 30 min) : c'est un
// filet, pas un cas courant. Seuil à 4 et pas moins, sinon on replie une ligne
// unique, ce qui coûte un clic sans rien gagner.
const MAX_VISIBLE_GROUPS = 4;

// Icône booster = l'illustration maison boost.svg (rouge/jaune, fiery).
function BoostIcon({ size = 16, className = "" }) {
    return (
        <Image
            src="/static/icons/boost.svg"
            width={Math.round(size * 0.62)}
            height={size}
            alt=""
            className={className}
        />
    );
}

export default function RepsBoosterCard() {
    const t = useTranslations();
    const [showAllGroups, setShowAllGroups] = useState(false);
    const [activatingId, setActivatingId] = useState(null);
    const [, setTick] = useState(0);
    const { state, active, inventory, activate: activateBooster } =
        useXpBoosters();

    // Tic chaque seconde pour le décompte du booster actif.
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    if (!state) return null;
    if (!active && inventory.length === 0) return null;

    const groups = groupBoosters(inventory);
    const visibleGroups = showAllGroups
        ? groups
        : groups.slice(0, MAX_VISIBLE_GROUPS);

    async function activate(id) {
        setActivatingId(id);
        try {
            const ok = await activateBooster(id);
            if (!ok) {
                toast.error(t("reps_boosters_one_active"));
                return;
            }
            toast.success(t("reps_boosters_activated"));
        } catch (e) {
            toast.error(t("reps_boosters_one_active"));
        } finally {
            setActivatingId(null);
        }
    }

    return (
        <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="mb-2 flex items-center gap-1.5">
                <BoostIcon size={16} />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("reps_boosters_title")}
                </span>
            </div>

            {active && (
                <div className="mb-3 flex items-center justify-between rounded-lg bg-gradient-to-r from-orange-500 to-red-500 px-3 py-2 text-white">
                    <span className="text-sm font-black tabular-nums">
                        ×{active.multiplier}
                    </span>
                    <span className="text-xs font-semibold tabular-nums">
                        {t("reps_boosters_remaining", {
                            time: formatCountdown(active.activeUntil),
                        })}
                    </span>
                </div>
            )}

            {groups.length > 0 && (
                <ul className="space-y-1.5">
                    {visibleGroups.map((g) => (
                        <li
                            key={g.key}
                            className="rounded-lg border border-gray-100 px-2.5 py-1.5 dark:border-white/10"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="flex min-w-0 items-center gap-1.5 text-sm text-gray-700 dark:text-grayDark-200">
                                    <BoostIcon size={16} />
                                    <span className="font-semibold">×{g.multiplier}</span>
                                    <span className="text-xs text-gray-400">
                                        {g.durationMinutes} min
                                    </span>
                                    {/* Le compte n'a de sens qu'à partir de deux,
                                        et reste nu : un "×50" à côté du "×2" du
                                        multiplicateur se lirait comme un réglage
                                        du booster. */}
                                    {g.count > 1 && (
                                        <span
                                            title={t("reps_boosters_in_stock", {
                                                count: g.count,
                                            })}
                                            className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold tabular-nums text-gray-600 dark:bg-white/10 dark:text-grayDark-300"
                                        >
                                            {g.count}
                                        </span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => activate(g.nextId)}
                                    disabled={!!active || activatingId === g.nextId}
                                    className="shrink-0 rounded-md bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {activatingId === g.nextId
                                        ? t("reps_boosters_activating")
                                        : t("reps_boosters_activate")}
                                </button>
                            </div>

                            {/* Péremption : on ne le dit qu'au moment où c'est
                                actionnable, sinon c'est du bruit permanent.
                                L'échéance annoncée est celle du seuil, pas
                                celle du plus urgent : dire "9 expirent dans 12
                                heures" quand un seul part dans 12 heures et les
                                autres dans trois jours serait faux. */}
                            {g.expiringSoon > 0 && (
                                <p className="mb-0 mt-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                                    {t("reps_boosters_expiring", {
                                        count: g.expiringSoon,
                                        days: EXPIRY_WARNING_DAYS,
                                    })}
                                </p>
                            )}
                        </li>
                    ))}

                    {groups.length > MAX_VISIBLE_GROUPS && (
                        <li>
                            <button
                                type="button"
                                onClick={() => setShowAllGroups((v) => !v)}
                                className="w-full rounded-md py-1 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-800 dark:text-grayDark-400 dark:hover:text-white"
                            >
                                {showAllGroups
                                    ? t("reps_boosters_show_less")
                                    : t("reps_boosters_show_all", {
                                          count: groups.length,
                                      })}
                            </button>
                        </li>
                    )}
                </ul>
            )}

            <p className="mt-2 text-xs text-gray-400">
                {t("reps_boosters_caption")}
            </p>
        </div>
    );
}
